import { usePostApiCsvSchemas } from '@/api/gen/api';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Add, Delete, ArrowBack, Save, TableChart } from '@mui/icons-material';
import { CsvDataType } from '@/generated/data-contracts';
import HierarchicalFilter from '@/components/HierarchicalFilter';

interface ColumnDefinition {
  header: string;
  dataType: CsvDataType;
  isRequired: boolean;
  isPrimary: boolean;
  isTimestamp: boolean;
}

export default function CreateCsvSchema() {
  const navigate = useNavigate();
  const { mutate: createCsvSchema, isLoading: isPending } =
    usePostApiCsvSchemas();

  const [schemaName, setSchemaName] = useState('');
  const [plantId, setPlantId] = useState<number | undefined>();
  const [columns, setColumns] = useState<ColumnDefinition[]>([
    {
      header: '',
      dataType: CsvDataType.String,
      isRequired: false,
      isPrimary: false,
      isTimestamp: false,
    },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handlePlantChange = (selections: { plantId?: number }) => {
    setPlantId(selections.plantId);
  };

  const handleAddColumn = () => {
    setColumns([
      ...columns,
      {
        header: '',
        dataType: CsvDataType.String,
        isRequired: false,
        isPrimary: false,
        isTimestamp: false,
      },
    ]);
  };

  const handleRemoveColumn = (index: number) => {
    if (columns.length > 1) {
      const newColumns = columns.filter((_, i) => i !== index);
      setColumns(newColumns);
    }
  };

  const handleColumnChange = (
    index: number,
    field: keyof ColumnDefinition,
    value: any
  ) => {
    const newColumns = [...columns];

    // Si se está marcando isTimestamp, desmarcar todas las demás
    if (field === 'isTimestamp' && value === true) {
      newColumns.forEach((col, i) => {
        if (i !== index) {
          col.isTimestamp = false;
        }
      });
    }

    // Si se cambia el tipo de dato y no es DateTime, desmarcar isTimestamp
    if (field === 'dataType' && value !== CsvDataType.DateTime) {
      newColumns[index].isTimestamp = false;
    }

    newColumns[index] = {
      ...newColumns[index],
      [field]: value,
    };
    setColumns(newColumns);
  };

  const validateForm = (): boolean => {
    if (!schemaName.trim()) {
      setError('El nombre del esquema es requerido');
      return false;
    }
    if (!plantId) {
      setError('Debe seleccionar una planta');
      return false;
    }
    if (columns.length === 0) {
      setError('Debe agregar al menos una columna');
      return false;
    }
    for (let i = 0; i < columns.length; i++) {
      if (!columns[i].header.trim()) {
        setError(`La columna ${i + 1} debe tener un encabezado`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    setError(null);
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    createCsvSchema(
      {
        data: {
          name: schemaName,
          plantId: plantId!,
          columns: columns,
        },
      },
      {
        onSuccess: response => {
          const createdSchemaId = response.id;
          // Navegar al show del nuevo schema con un parámetro de estado
          navigate(`/CsvSchemas/${createdSchemaId}/show`, {
            state: { showSuccessMessage: true },
          });
        },
        onError: (err: any) => {
          setError(err?.message || 'Error al crear el esquema CSV');
        },
      }
    );
  };

  const dataTypes = Object.values(CsvDataType);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/CsvSchemas')}
          variant='outlined'
        >
          Volver
        </Button>
        <Typography variant='h4' component='h1'>
          Crear Nuevo Esquema CSV
        </Typography>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity='success' sx={{ mb: 2 }}>
          ¡Esquema creado exitosamente! Redirigiendo...
        </Alert>
      )}

      {/* Información General */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant='h5' gutterBottom sx={{ mb: 3 }}>
          Información General
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Nombre del Esquema'
              value={schemaName}
              onChange={e => setSchemaName(e.target.value)}
              required
              placeholder='Ej: Esquema de Producción'
              helperText='Máximo 200 caracteres'
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Seleccionar Planta *
              </Typography>
              <HierarchicalFilter
                levels={['plant']}
                onChange={handlePlantChange}
                size='small'
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Columnas */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant='h5'>
            <TableChart sx={{ mr: 1, verticalAlign: 'middle' }} />
            Columnas del Esquema
          </Typography>
          <Button
            variant='contained'
            startIcon={<Add />}
            onClick={handleAddColumn}
            color='primary'
          >
            Añadir Columna
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  #
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Encabezado *
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Tipo de Dato *
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ color: 'white', fontWeight: 'bold' }}
                >
                  Requerido
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ color: 'white', fontWeight: 'bold' }}
                >
                  Clave Primaria
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ color: 'white', fontWeight: 'bold' }}
                >
                  Timestamp
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ color: 'white', fontWeight: 'bold' }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.map((column, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography fontWeight='bold'>{index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size='small'
                      value={column.header}
                      onChange={e =>
                        handleColumnChange(index, 'header', e.target.value)
                      }
                      placeholder='Nombre de la columna'
                      required
                      inputProps={{ maxLength: 128 }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size='small'>
                      <Select
                        value={column.dataType}
                        onChange={e =>
                          handleColumnChange(
                            index,
                            'dataType',
                            e.target.value as CsvDataType
                          )
                        }
                      >
                        {dataTypes.map(type => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align='center'>
                    <Checkbox
                      checked={column.isRequired}
                      onChange={e =>
                        handleColumnChange(
                          index,
                          'isRequired',
                          e.target.checked
                        )
                      }
                      color='error'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Checkbox
                      checked={column.isPrimary}
                      onChange={e =>
                        handleColumnChange(index, 'isPrimary', e.target.checked)
                      }
                      color='primary'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Checkbox
                      checked={column.isTimestamp}
                      onChange={e =>
                        handleColumnChange(
                          index,
                          'isTimestamp',
                          e.target.checked
                        )
                      }
                      disabled={column.dataType !== CsvDataType.DateTime}
                      color='warning'
                      title={
                        column.dataType !== CsvDataType.DateTime
                          ? 'Solo disponible para columnas de tipo DateTime'
                          : 'Marcar como columna de timestamp (solo una permitida)'
                      }
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      color='error'
                      onClick={() => handleRemoveColumn(index)}
                      disabled={columns.length === 1}
                      title='Eliminar columna'
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {columns.length === 0 && (
          <Alert severity='info' sx={{ mt: 2 }}>
            No hay columnas definidas. Haga clic en "Añadir Columna" para
            empezar.
          </Alert>
        )}
      </Paper>

      {/* Resumen */}
      <Card sx={{ mb: 3, bgcolor: 'info.lighter' }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            📊 Resumen
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='text.secondary'>
                Nombre:
              </Typography>
              <Typography fontWeight='bold'>
                {schemaName || '(Sin nombre)'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='text.secondary'>
                Planta ID:
              </Typography>
              <Typography fontWeight='bold'>
                {plantId || '(No seleccionada)'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='text.secondary'>
                Total Columnas:
              </Typography>
              <Typography fontWeight='bold'>{columns.length}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='text.secondary'>
                Columnas Requeridas:
              </Typography>
              <Typography fontWeight='bold'>
                {columns.filter(col => col.isRequired).length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botones de Acción */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant='outlined'
          onClick={() => navigate('/CsvSchemas')}
          disabled={isPending}
        >
          Cancelar
        </Button>
        <Button
          variant='contained'
          startIcon={<Save />}
          onClick={handleSubmit}
          disabled={isPending}
          size='large'
        >
          {isPending ? 'Guardando...' : 'Crear Esquema'}
        </Button>
      </Box>
    </Box>
  );
}
